import { PawPrint } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

import type { EchoflicksSessionEvent } from "../types";
import { buildPetAdaptationAudit } from "./petAdaptation";

const label = (value?: string) =>
  value
    ? value.replaceAll("_", " ").replace(/^./, (first) => first.toUpperCase())
    : "Not recorded";

export function PetAdaptationCard({
  events,
}: {
  events: EchoflicksSessionEvent[];
}) {
  const audit = buildPetAdaptationAudit(events);
  if (!audit) return null;

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <PawPrint className="size-4" aria-hidden="true" />
            Pet identity adaptation
          </CardTitle>
          <div className="flex gap-2">
            <Badge variant="outline">{label(audit.lifecycleState)}</Badge>
            {audit.adapterSetState ? (
              <Badge variant="secondary">
                Adapters: {label(audit.adapterSetState)}
              </Badge>
            ) : null}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        <dl className="grid gap-3 sm:grid-cols-3">
          <div>
            <dt className="text-muted-foreground">Adaptation choice</dt>
            <dd>{label(audit.consentStatus)}</dd>
          </div>
          <div>
            <dt className="text-muted-foreground">Policy mode</dt>
            <dd>{label(audit.policyMode)}</dd>
          </div>
          <div>
            <dt className="text-muted-foreground">Training cost</dt>
            <dd>
              {audit.runpodCostUsd || audit.totalCostUsd
                ? `$${audit.runpodCostUsd ?? audit.totalCostUsd}`
                : "Not reported"}
            </dd>
          </div>
        </dl>
        {audit.whyNeutral ? (
          <div className="rounded-md border border-amber-500/30 bg-amber-500/5 p-3">
            <p className="font-medium">Why neutral?</p>
            <p className="text-muted-foreground">
              {label(audit.whyNeutral.reason)} at{" "}
              {label(audit.whyNeutral.stage)}
              {audit.whyNeutral.downstreamReasons.length
                ? `. Then: ${audit.whyNeutral.downstreamReasons.map(label).join(", ")}.`
                : "."}
            </p>
          </div>
        ) : null}
        {audit.observationCount ? (
          <p className="text-muted-foreground">
            Scenes: {audit.eligibleScenes} eligible / {audit.adaptedScenes}{" "}
            adapted / {audit.fallbackScenes} fallback / {audit.failedScenes}{" "}
            failed
          </p>
        ) : null}
        {audit.dogs.length ? (
          <div className="overflow-x-auto rounded-md border">
            <table className="w-full text-left text-xs">
              <thead className="bg-muted/50 text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 font-medium">Dog</th>
                  <th className="px-3 py-2 font-medium">Dataset</th>
                  <th className="px-3 py-2 font-medium">Training</th>
                  <th className="px-3 py-2 font-medium">Time</th>
                  <th className="px-3 py-2 font-medium">Scenes</th>
                  <th className="px-3 py-2 font-medium">Identity QC</th>
                </tr>
              </thead>
              <tbody>
                {audit.dogs.map((dog, index) => (
                  <tr key={dog.identityRef} className="border-t">
                    <td className="px-3 py-2">Dog {index + 1}</td>
                    <td className="px-3 py-2">
                      {dog.datasetImages == null
                        ? "—"
                        : `${dog.datasetImages} images`}
                    </td>
                    <td className="px-3 py-2">
                      {label(dog.trainingState ?? dog.adapterState)}
                    </td>
                    <td className="px-3 py-2">
                      {dog.trainerSeconds == null
                        ? "—"
                        : `${dog.trainerSeconds.toFixed(2)}s GPU`}
                    </td>
                    <td className="px-3 py-2">
                      {dog.boundScenes} adapted / {dog.fallbackScenes} fallback
                      / {dog.failedScenes} failed
                    </td>
                    <td className="px-3 py-2">
                      {dog.qcAccepted} pass / {dog.qcRejected} reject
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-muted-foreground">
            No per-dog training job was admitted.
          </p>
        )}
        {audit.deletionState ? (
          <p className="text-muted-foreground">
            Deletion: {label(audit.deletionState)}
          </p>
        ) : null}
      </CardContent>
    </Card>
  );
}
